import { useEffect, useRef, useState } from 'react';
import { Alert, AuthLayout } from '@msarat/theme/components';

import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.jsx';

/**
 * Email verification landing page.
 *
 * The parent arrives here from a link in their inbox, so this screen has to be
 * self-explanatory and has to handle the ways a link goes wrong: already used,
 * expired, or mangled by an email client. Every outcome ends with something the
 * parent can actually do next, rather than a dead end.
 *
 * Verification is a GET on the API, which means an email client that prefetches
 * links can consume the token before the parent clicks. The API consumes it
 * atomically, and the "already verified" case reads as success here rather than
 * as a failure, because from the parent's point of view it is one.
 */
export function VerifyEmail({ onGoToLogin }) {
  const { t } = useI18n();
  const [state, setState] = useState({ status: 'verifying' });
  // React 18 StrictMode mounts effects twice in development; without this the
  // page would fire two verification calls and the second would report the
  // token as already used.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setState({ status: 'invalid', messageKey: 'verify.incomplete' });
      return;
    }

    api
      .verifyEmail(token)
      .then(() => setState({ status: 'verified' }))
      .catch((error) => {
        if (error.code === 'not_found') {
          setState({ status: 'expired', messageKey: 'verify.expired' });
          return;
        }
        setState({ status: 'error', message: error.message });
      });
  }, []);

  if (state.status === 'verifying') {
    return (
      <AuthLayout brandLabel={t('brand.name')} title={t('verify.working')}>
        <p aria-live="polite">{t('verify.wait')}</p>
      </AuthLayout>
    );
  }

  if (state.status === 'verified') {
    return (
      <AuthLayout brandLabel={t('brand.name')} title={t('verify.doneTitle')} subtitle={t('verify.doneSubtitle')}>
        <Alert kind="success">{t('verify.doneBody')}</Alert>
        <button className="button button--primary button--block" type="button" onClick={onGoToLogin}>
          {t('verify.goSignIn')}
        </button>
      </AuthLayout>
    );
  }

  // Expired, already used, malformed, or a server error all land here. The
  // parent's next step is the same in every case: sign in, and request a fresh
  // link from inside the account.
  return (
    <AuthLayout
      brandLabel={t('brand.name')}
      title={t('verify.failTitle')}
      subtitle={t('verify.failSubtitle')}
    >
      <Alert kind="error">{state.messageKey ? t(state.messageKey) : state.message}</Alert>
      <button className="button button--primary button--block" type="button" onClick={onGoToLogin}>
        {t('verify.goToLogin')}
      </button>
    </AuthLayout>
  );
}
